import { ProcedureStatus, supplyConditionLabels } from '@caresend/types';
import { camelCaseToSentenceCase, capitalize } from '@caresend/utils';
import { defineComponent, ref, computed, watch } from 'vue';
import __vue_component__ from '../../../alerts/Alert.vue.js';
import __vue_component__$1 from '../../../alerts/Pane/Pane.vue.js';
import { SupportedDynamicCopyComponent } from '../../../dynamicCopy/general/model.js';
import __vue_component__$3 from '../../../inputs/Radio/Radio.vue.js';
import __vue_component__$4 from '../../../inputs/SelectComponent.vue.js';
import __vue_component__$5 from '../../../inputs/TextInput/TextInput.vue.js';
import __vue_component__$2 from '../../../layout/InfoSubtitle.vue.js';
import { cancelProcedure } from '../../portal/components/ItineraryWaypoint/components/WaypointProcedureCard/procedureCancelation.js';
import { usePortalStore } from '../../portal/store/portal/index.js';
import { isCancelationUserType, isCancelationReason, isSupplyCondition, saveProceduresStatus } from './helpers.js';
import 'lodash.isequal';
import '../../../../router/helpers/initRouter.js';
import { getStore } from '../../../../store/helpers/initStore.js';
import { useUserRole } from '../../../../composables/user.js';
import '@sentry/vue';
import { toastError } from '../../../../functions/toast/toast.js';
import '@fortawesome/fontawesome-svg-core';
import 'buefy';
import '../../../ui/Icon.vue.js';
import '../../../../functions/configuration/icons.js';
import '../../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../../functions/tracking/index.js';
import { useOrdersStore } from '../../../../store/pinia/orders/index.js';

var script = defineComponent({
    name: 'ProcedureCancelationAlert',
    components: {
        Alert: __vue_component__,
        Pane: __vue_component__$1,
        InfoSubtitle: __vue_component__$2,
        Radio: __vue_component__$3,
        SelectComponent: __vue_component__$4,
        TextInput: __vue_component__$5,
    },
    props: {
        componentType: {
            type: String,
            default: 'Alert',
        },
        orderID: {
            type: String,
            default: null,
        },
        procedureIDs: {
            type: Array,
            default: () => [],
        },
        visible: {
            type: Boolean,
            required: true,
        },
        status: {
            type: String,
            default: ProcedureStatus.CANCELED,
        },
        draftWaypointID: {
            type: String,
            default: undefined,
        },
        saveWithRecordManager: {
            type: Boolean,
            default: false,
        },
        customSave: Boolean,
        allowUserTypeSelection: Boolean,
    },
    setup(props, { emit }) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        const { isAssistant, isPrescriber, role } = useUserRole();
        const store = getStore();
        const ordersStore = useOrdersStore();
        const portalStore = usePortalStore();
        const isLoading = ref(false);
        const singleProcedureID = computed(() => props.procedureIDs.length === 1 ? props.procedureIDs[0] : undefined);
        const singleProcedure = computed(() => singleProcedureID.value
            ? portalStore.procedures.get(singleProcedureID.value)
            : undefined);
        const userTypeSelected = ref((_b = (_a = singleProcedure.value) === null || _a === void 0 ? void 0 : _a.procedureCanceled) === null || _b === void 0 ? void 0 : _b.userType);
        const reasonSelected = ref((_d = (_c = singleProcedure.value) === null || _c === void 0 ? void 0 : _c.procedureCanceled) === null || _d === void 0 ? void 0 : _d.reason);
        const supplyConditionSelected = ref((_f = (_e = singleProcedure.value) === null || _e === void 0 ? void 0 : _e.procedureCanceled) === null || _f === void 0 ? void 0 : _f.supplyCondition);
        const detailedReason = ref((_j = (_h = (_g = singleProcedure.value) === null || _g === void 0 ? void 0 : _g.procedureCanceled) === null || _h === void 0 ? void 0 : _h.detailedReason) !== null && _j !== void 0 ? _j : '');
        const isPrescriberOrAssistant = computed(() => isPrescriber.value || isAssistant.value);
        const cancelationTemplate = computed(() => {
            var _a, _b, _c, _d;
            return role.value
                ? (_d = (_c = (_b = (_a = store.state.variables.variables) === null || _a === void 0 ? void 0 : _a.dynamicCopy) === null || _b === void 0 ? void 0 : _b.forms) === null || _c === void 0 ? void 0 : _c.procedureCancelation) === null || _d === void 0 ? void 0 : _d[role.value]
                : undefined;
        });
        const detailedReasonTitle = computed(() => { var _a, _b, _c; return (_c = (_b = (_a = cancelationTemplate.value) === null || _a === void 0 ? void 0 : _a.detailedReason) === null || _b === void 0 ? void 0 : _b.title) !== null && _c !== void 0 ? _c : 'Detailed reason'; });
        const getSupplyConditionTitle = (supplyCondition) => {
            var _a, _b;
            const supplyConditionOption = (_a = cancelationTemplate.value) === null || _a === void 0 ? void 0 : _a.supplyCondition.options.find((option) => option.value === supplyCondition);
            return (_b = supplyConditionOption === null || supplyConditionOption === void 0 ? void 0 : supplyConditionOption.title) !== null && _b !== void 0 ? _b : '';
        };
        const collectValidTypes = (arr, typeValidationFn) => arr.reduce((acc, option) => {
            if (typeValidationFn(option.value)) {
                acc.push(option.value);
            }
            return acc;
        }, []);
        const userTypes = computed(() => { var _a, _b; return collectValidTypes((_b = (_a = cancelationTemplate.value) === null || _a === void 0 ? void 0 : _a.userType.options) !== null && _b !== void 0 ? _b : [], isCancelationUserType); });
        const reasons = computed(() => { var _a, _b; return collectValidTypes((_b = (_a = cancelationTemplate.value) === null || _a === void 0 ? void 0 : _a.reasons.options) !== null && _b !== void 0 ? _b : [], isCancelationReason); });
        const supplyConditions = computed(() => { var _a, _b; return collectValidTypes((_b = (_a = cancelationTemplate.value) === null || _a === void 0 ? void 0 : _a.supplyCondition.options) !== null && _b !== void 0 ? _b : [], isSupplyCondition); });
        const userTypeValid = computed(() => {
            var _a, _b;
            return ((_b = (_a = cancelationTemplate.value) === null || _a === void 0 ? void 0 : _a.userType) === null || _b === void 0 ? void 0 : _b.display)
                ? !!userTypeSelected.value
                : true;
        });
        const detailedReasonValid = computed(() => {
            var _a, _b, _c, _d;
            return ((_b = (_a = cancelationTemplate.value) === null || _a === void 0 ? void 0 : _a.detailedReason) === null || _b === void 0 ? void 0 : _b.display) && ((_d = (_c = cancelationTemplate.value) === null || _c === void 0 ? void 0 : _c.detailedReason) === null || _d === void 0 ? void 0 : _d.required)
                ? !!detailedReason.value
                : true;
        });
        const supplyConditionValid = computed(() => {
            var _a, _b;
            return ((_b = (_a = cancelationTemplate.value) === null || _a === void 0 ? void 0 : _a.supplyCondition) === null || _b === void 0 ? void 0 : _b.display)
                ? !!supplyConditionSelected.value
                : true;
        });
        const confirmDisabled = computed(() => !userTypeValid.value
            || !reasonSelected.value
            || !detailedReasonValid.value
            || !supplyConditionValid.value);
        const labelSupplyCondition = computed(() => supplyConditionSelected.value
            ? supplyConditionLabels[supplyConditionSelected.value]
            : null);
        const resetSelection = () => {
            userTypeSelected.value = undefined;
            supplyConditionSelected.value = undefined;
            reasonSelected.value = undefined;
            detailedReason.value = '';
            isLoading.value = false;
        };
        const close = () => {
            resetSelection();
            emit('close');
        };
        const saveProcedureStatusEvent = async () => {
            var _a, _b, _c, _d;
            try {
                isLoading.value = true;
                if (props.customSave) {
                    emit('save', {
                        userType: userTypeSelected.value,
                        reason: reasonSelected.value,
                        supplyCondition: supplyConditionSelected.value,
                    });
                    close();
                    return;
                }
                if (props.saveWithRecordManager) {
                    const procedureID = props.procedureIDs[0];
                    const canceledByRole = (_a = role.value) !== null && _a !== void 0 ? _a : (_b = store.state.auth.user) === null || _b === void 0 ? void 0 : _b.role;
                    if (!procedureID || !userTypeValid.value || !canceledByRole)
                        return;
                    cancelProcedure(procedureID, {
                        canceledBy: (_c = userTypeSelected.value) !== null && _c !== void 0 ? _c : '',
                        canceledByRole,
                        reason: reasonSelected.value,
                        detailedReason: detailedReason.value,
                        supplyCondition: supplyConditionSelected.value,
                        userType: userTypeSelected.value,
                    });
                }
                else if (props.orderID) {
                    const order = ordersStore.getters.getDenormalizedOrderByID(props.orderID);
                    if (!order)
                        return;
                    const procedureIDtoCancel = (_d = Object.values(props.procedureIDs)[0]) !== null && _d !== void 0 ? _d : '';
                    if (order.isDraft || !procedureIDtoCancel) {
                        ordersStore.cancelOrder({
                            orderID: props.orderID,
                            reason: reasonSelected.value,
                            detailedReason: detailedReason.value,
                            supplyCondition: supplyConditionSelected.value,
                            userType: userTypeSelected.value,
                        });
                    }
                    else {
                        // TODO: This was already broken but there seems to be a bug when
                        // we use props.orderID (OrdersPage in pro, VisitsPage in ui-c)
                        // await store.dispatch('orders/cancelDenormalizedOrderProcedure', {
                        //   orderID: props.orderID,
                        //   procedureID: procedureIDtoCancel,
                        //   reason: reasonSelected.value,
                        //   detailedReason: detailedReason.value,
                        //   supplyCondition: supplyConditionSelected.value,
                        //   userType: userTypeSelected.value,
                        // });
                    }
                }
                else {
                    await saveProceduresStatus({
                        draftWaypointID: props.draftWaypointID,
                        procedureIDs: props.procedureIDs,
                        reasonSelected: reasonSelected.value,
                        detailedReason: detailedReason.value,
                        supplyConditionSelected: supplyConditionSelected.value,
                        status: props.status,
                        userTypeSelected: userTypeSelected.value,
                    });
                }
                emit('doneSave');
                close();
            }
            catch (error) {
                toastError(error);
            }
            finally {
                isLoading.value = false;
            }
        };
        const bodyClass = computed(() => {
            const classes = [];
            if (props.componentType === 'Alert') {
                classes.push('ProcedureCancelationAlert__body--alert');
            }
            else if (props.componentType === 'Pane') {
                classes.push('ProcedureCancelationAlert__body--pane');
            }
            return classes;
        });
        const componentProps = computed(() => {
            if (props.componentType === 'div') {
                return {};
            }
            return {
                isBottomBoundMobile: true,
                isOpened: props.visible,
                title: 'Request cancelation',
                textButton: 'Confirm cancelation',
                textButtonCancel: 'Back',
                confirmDanger: true,
                confirmDisabled: confirmDisabled.value,
                isLoading: isLoading.value,
            };
        });
        watch(reasonSelected, (newReason) => {
            emit('update:reasonSelected', newReason);
        });
        return {
            SupportedDynamicCopyComponent,
            bodyClass,
            camelCaseToSentenceCase,
            cancelationTemplate,
            capitalize,
            close,
            componentProps,
            confirmDisabled,
            detailedReason,
            detailedReasonTitle,
            getSupplyConditionTitle,
            isLoading,
            isPrescriberOrAssistant,
            labelSupplyCondition,
            reasonSelected,
            reasons,
            saveProcedureStatusEvent,
            supplyConditionSelected,
            supplyConditions,
            userTypeSelected,
            userTypes,
        };
    },
});

export { script as default };
